import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  RotateCw,
  ZoomIn,
  ZoomOut,
} from "lucide-react"
import { useState, useEffect } from "react"
import { Document, Page, pdfjs } from "react-pdf"

import { useMediaQuery } from "@/hooks/use-media-query"
import { cn } from "@/utils/classes"

import { Button } from "../button"
import { Slider } from "../control"
import { ScrollArea, ScrollBar } from "../surfaces"

import "react-pdf/dist/Page/AnnotationLayer.css"
import "react-pdf/dist/Page/TextLayer.css"

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`

interface PDFViewerProps {
  file: string
  className?: string
}

export function PDFViewer({ file, className }: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale, setScale] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const isMobile = useMediaQuery("(max-width: 768px)")

  useEffect(() => {
    // Reset state when file changes
    setNumPages(null)
    setPageNumber(1)
    setScale(isMobile ? 0.5 : 1)
    setRotation(0)
    setLoading(true)
    setError(null)
  }, [file, isMobile])

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages)
    setLoading(false)
  }

  function onDocumentLoadError(error: Error) {
    setError(error)
    setLoading(false)
  }

  function previousPage() {
    setPageNumber((prev) => Math.max(prev - 1, 1))
  }

  function nextPage() {
    setPageNumber((prev) => (numPages ? Math.min(prev + 1, numPages) : prev))
  }

  function zoomIn() {
    setScale((prev) => Math.min(prev + 0.2, 3))
  }

  function zoomOut() {
    setScale((prev) => Math.max(prev - 0.2, 0.5))
  }

  function rotate() {
    setRotation((prev) => (prev + 90) % 360)
  }

  return (
    <div className={cn("flex flex-col size-full", className)}>
      <div className="flex flex-col h-full">
        <ScrollArea className="flex-1 relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/50">
              <Loader2 className="size-8 animate-spin text-primary" />
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-destructive/10 text-destructive p-4 rounded-md max-w-md text-center">
                <p className="font-medium">Error loading PDF</p>
                <p className="text-sm mt-2">{error.message}</p>
              </div>
            </div>
          )}

          <div className="flex justify-center min-h-[400px] p-4">
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
              className="flex justify-center"
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                rotate={rotation}
                loading={null}
                className="shadow-lg"
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          </div>

          <ScrollBar orientation="horizontal" />
        </ScrollArea>

        <div className="p-4 border-t bg-muted/30">
          <div className="flex flex-wrap gap-4 lg:justify-between items-center justify-center">
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="icon"
                onClick={previousPage}
                disabled={pageNumber <= 1}
                aria-label="Previous page"
              >
                <ChevronLeft className="size-4" />
              </Button>

              <span className="text-sm">
                {pageNumber} / {numPages || "?"}
              </span>

              <Button
                variant="outline"
                size="icon"
                onClick={nextPage}
                disabled={!numPages || pageNumber >= numPages}
                aria-label="Next page"
              >
                <ChevronRight className="size-4" />
              </Button>
            </div>

            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="icon"
                onClick={zoomOut}
                disabled={scale <= 0.5}
                aria-label="Zoom out"
              >
                <ZoomOut className="size-4" />
              </Button>

              <div className="w-32">
                <Slider
                  value={[scale * 100]}
                  min={50}
                  max={300}
                  step={10}
                  onValueChange={(value) => setScale(value[0] / 100)}
                  aria-label="Zoom level"
                />
              </div>

              <Button
                variant="outline"
                size="icon"
                onClick={zoomIn}
                disabled={scale >= 3}
                aria-label="Zoom in"
              >
                <ZoomIn className="size-4" />
              </Button>

              <Button
                variant="outline"
                size="icon"
                onClick={rotate}
                aria-label="Rotate page"
              >
                <RotateCw className="size-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
